import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { META_DATA } from 'src/services/constants';
import { pathOr, lensPath, set, isEmpty, lensProp, join, equals, find, propEq, and, gt, not } from 'ramda';

import { compose } from 'redux';
import { connect } from 'react-redux';

import Box from '@mui/system/Box';

import ChevronLeftIcon from '../../../assets/icons/chevron-left.svg';

import Button from 'src/components/Buttons/Button';
import withAdvisorProfile from 'src/containers/advisorProfile.containers';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Theme, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SummaryInput from './SummaryTextInput';
import { updateAdvisorProfile } from 'src/services/advisor';
import { meaningfulNameInEmail } from 'src/utilities/dashboard';
import { updateAccountInfo, updateJustPassword } from 'src/services/account';
import { MapState } from 'src/store/types';
import useNotification from 'src/features/ToastNotifications/ToastNotifications';
import { PHONE_NUMBER_TEXT } from 'src/features/core/Intents/intentConstants';
import { buildLabel } from 'src/utilities/naming';
import { FALSE, TRUE } from 'src/constants';
import SiteSettings from 'src/siteSetting';
import InAppNotificationToggle from './InAppNotificationToggle';
import SummaryLink from './SummaryLink';
import { filterIsNotEmpty, isNotEmpty } from 'src/utilities/hasValue';
import { checkValidPassword, validatePassword } from 'src/utilities/password';
import { MIN_STRONG } from 'src/utilities/notifications/accounts';
import { Account } from 'src/types/Account';
import { AdvisorProfile } from 'src/types/Advisor';

import TimeZones from './TimeZonesForm';
import { authenticatedUser } from 'src/utilities/storage';
import { renderTimeZonesList } from 'src/utilities/timezones/utilities';
import { pruneAddress } from 'src/utilities/properties';
import AddressDropdown from 'src/components/AddressDropdown/AddressDropdown';

const useMenuStyles = makeStyles((theme: Theme) =>
  createStyles({
    goBackBtn: {
      top: 16,
      left: 10,
      position: 'absolute',
      [theme.breakpoints.down('sm')]: {
        left: 0,
        paddingLeft: '16px !important'
      }
    },
    dialogTitle: {
      textAlign: 'center',
      fontFamily: 'Martel,serif',
      [theme.breakpoints.down('sm')]: {
        marginTop: '24px'
      }
    },
    dialogHeader: {
      borderTop: '1px solid #EEE',
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      padding: 20,
      background: theme.palette.mainLight,
      border: `1px solid ${theme.palette.gray.extraLightGray}`,
      boxShadow: '0px 5px 7px rgba(0, 0, 0, 0.05)',
      borderRadius: 5,
      width: '100%'
    },
    profileMetaContainer: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 20,
    },
    passwordTextField: {
      '& .MuiInputBase-input-MuiOutlinedInput-input': {
        padding: 0
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    }
  })
);

type Props = {
  openProfile: boolean;
  setOpenProfile: () => void;
};

interface WithAdvisorProps {
  advisorProfile: any;
}

type CombinedProps = Props & StateProps & WithAdvisorProps;

const AdvisorProfilePopup: React.FC<CombinedProps> = (props: CombinedProps) => {
  const { openProfile, setOpenProfile, advisorProfile, accounts } = props
  const [isEdit, setEdit] = useState(false);
  const [updateErrors, setUpdateErrors] = useState([]);
  const [sendNotification] = useNotification();
  const classes = useMenuStyles();
  const timezoneList = renderTimeZonesList();

  const first_name = pathOr('', ['first_name'], advisorProfile);
  const last_name = pathOr('', ['last_name'], advisorProfile);
  const email = pathOr('', ['email'], advisorProfile);
  const user_name = pathOr('', ['user_name'], advisorProfile);
  const business_name = pathOr('', ['business_name'], advisorProfile);
  const phone = pathOr('', ['phone'], advisorProfile);
  const profileAddress = pathOr({}, ['address'], advisorProfile);
  const addressObj = pathOr(profileAddress, ['address'], accounts);
  const currentAddress = filterIsNotEmpty([
    pathOr('', ['street'], addressObj),
    pathOr('', ['city'], addressObj),
    pathOr('', ['state'], addressObj),
    pathOr('', ['zip_code'], addressObj)
  ])
  const business_location = isNotEmpty(currentAddress) ? join(', ', currentAddress) : '';

  const title = pathOr('', [META_DATA, buildLabel('title')], accounts)
  const timeZone = pathOr('', [META_DATA, buildLabel('time_zone')], accounts)
  const userEnableInAppNotification = pathOr(FALSE, [META_DATA, buildLabel('enable_inapp_notification')], accounts);
  const hasInAppCapability = equals(SiteSettings.generalInAppNotification(), TRUE)
  const [enableInAppNotification, setEnableInAppNotification] = useState(userEnableInAppNotification)
  const [phoneNumber, setPhoneNumber] = useState(phone)
  const [password, setPassword] = useState('');
  const [inputValue, setValue] = useState({
    firstName: first_name,
    lastName: last_name,
    email: email,
    userName: user_name,
    businessName: business_name,
    businessLocation: business_location,
    timeZone: pathOr('', ['label'], find(propEq('value', timeZone), timezoneList))
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setValue((prevState: any) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleValueChange = (value: any, name: string) => {
    setValue((prevState: any) => ({
      ...prevState,
      [name]: value
    }));
  }

  const handleDropdownChange = (value: string, name: string) => {
    setValue((prevState: any) => ({
      ...prevState,
      [name]: value
    }));
  }
  const handlePhoneChange = (value: any) => {
    setPhoneNumber(value);
  };



  const handleSubmitAllrequest = (requests: (Promise<Account> | Promise<AdvisorProfile>)[]) => {
    return Promise.all(requests)
      .then((response) => {
        if (response) {
          authenticatedUser.accountTimeZone.set(timeZone)
          sendNotification({
            msg: 'Updated',
            variant: 'success',
          })
        }
        setPassword('')
      }).catch((errResponse) => {
        onErrorOfUser(errResponse);
      });
  }

  const updateAccountPassword = (passwordData: any) => {
    return updateJustPassword(passwordData)
        .then((response: any) => {
          return response;
        })
        .catch((errResponse: any) => {
          const errorMessage = pathOr('', ['response', 'data', 'message'], errResponse);
          if (errorMessage) {
            sendNotification({
              msg: errorMessage,
              variant: 'error',
            });
          }
          return errResponse;

    })
  }
  const onSubmit = () => {

    const prunedAddress: any = pruneAddress(inputValue.businessLocation);

    const updAddress = {
      city: pathOr('', ['city'], prunedAddress),
      country: pathOr('', ['country'], prunedAddress),
      state: pathOr('', ['state'], prunedAddress),
      street: pathOr('', ['street'], prunedAddress),
      zip_code: pathOr('', ['zip'], prunedAddress),
    }

    const data = compose(
      set(lensPath(['first_name']), inputValue.firstName || meaningfulNameInEmail(email)),
      set(lensPath(['last_name']), inputValue.lastName),
      set(lensPath(['user_name']), inputValue.userName),
      set(lensPath(['email']), inputValue.email),
      set(lensPath(['phone']), phoneNumber),
      set(lensPath(['business_name']), inputValue.businessName),
      set(lensPath([META_DATA, buildLabel('enable_inapp_notification')]), enableInAppNotification),
      set(lensPath([META_DATA, 'lendsmart_sh/business_location']), inputValue.businessLocation),
      set(lensPath(['address']), updAddress),
      set(lensPath([META_DATA, buildLabel('title')]), title),
      set(lensPath(['object_meta', 'name']), inputValue.userName),
    )(advisorProfile);

    const timeZone = pathOr('', ['value'], find(propEq('label', pathOr('', ['timeZone'], inputValue)), timezoneList));
    const passwordData = compose(
      set(lensPath(['id']), pathOr('', ['account_id'], advisorProfile)),
      set(lensPath(['email']), pathOr('', ['email'], accounts)),
      set(lensProp('password'), password),
    )({});
    const accountData = compose(
      set(lensPath(['first_name']), inputValue.firstName),
      set(lensPath(['last_name']), inputValue.lastName),
      set(lensPath(['email']), inputValue.email),
      set(lensPath(['cellmobile_phone']), phoneNumber),
      set(
        lensPath([META_DATA, buildLabel('enable_inapp_notification')]),
        enableInAppNotification,
      ),
      set(lensPath([META_DATA, buildLabel('time_zone')]), timeZone),
      set(lensPath(['address']), updAddress),
    )(accounts);

    const updatedValidators = validatePassword(password, MIN_STRONG);
    const isValidPassword = checkValidPassword(updatedValidators);
    if (and(gt(password.length, 0), isValidPassword)) {
      handleSubmitAllrequest([
        updateAccountInfo(accountData),
        updateAdvisorProfile(data),
        updateAccountPassword(passwordData)
      ]);
    }
    else if (and(gt(password.length, 0), not(isValidPassword))) {
      sendNotification({
        msg: `Password must be between ${MIN_STRONG} to 32 characters and at least 1 uppercase letter, 1 lowercase letter, 1 number and special characters.`,
        variant: 'error',
      });
    } else {
      handleSubmitAllrequest([updateAccountInfo(accountData), updateAdvisorProfile(data)]);
    }
  }

  const handleChangeInAppNotification = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEnableInAppNotification(
      equals(e.target.checked, true) ? TRUE : FALSE
    );
  }

  const handlePasswordChange = (value: any) => {
    setPassword(value);
  }

  // const handleClosePassword = () => {
  //   updatePassword()
  //   setEdit(false);
  // }

  const handleClose = () => {
    onSubmit()
    setEdit(false);
  };

  const onErrorOfUser = (errResponse: any) => {
    const formErrors = pathOr([], ['response', 'data', 'errors'], errResponse);
    if (isEmpty(formErrors)) {
      sendNotification({
        msg: "Not Updated",
        variant: 'error',
      });
    }
    setUpdateErrors(formErrors);
  };
  const handleSave = async () => {
    onSubmit();
    setEdit(false);
  };
  const { breakpoints } = useTheme();
  const matches = useMediaQuery(breakpoints.up('sm'));

  return (
    <Dialog open={openProfile} onClose={setOpenProfile} maxWidth="md" fullWidth>
      <Box className={classes.dialogHeader}>
        <DialogTitle className={classes.dialogTitle}>Profile</DialogTitle>

        <Button
          px={30}
          py={6}
          text="Go back"
          variant="text"
          onClick={setOpenProfile}
          startIcon={<ChevronLeftIcon />}
          className={classes.goBackBtn}
        />
      </Box>

      <DialogContent>
        <Grid flexDirection="column" container>
          <Box mb={2} className={classes.wrapper}>
            <Typography variant="h6" color="primaryColor" gutterBottom>
              Personal information
            </Typography>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  First name
                </Typography>
                <SummaryInput
                  value={inputValue.firstName}
                  onChange={handleChange}
                  type="text"
                  name="firstName"
                  isEdit={isEdit}
                  onClick={() => setEdit(true)}
                  onClose={handleClose}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography variant="caption" component="p" color="textSecondary">
                  Last name
                </Typography>
                <SummaryInput
                  value={inputValue.lastName}
                  onChange={handleChange}
                  type="text"
                  name="lastName"
                  isEdit={isEdit}
                  onClick={() => setEdit(true)}
                  onClose={handleClose}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  User name
                </Typography>
                <SummaryInput
                  value={inputValue.userName}
                  onChange={handleChange}
                  type="text"
                  name="userName"
                  isEdit={isEdit}
                  onClick={() => setEdit(true)}
                  onClose={handleClose}
                />
              </Box>
            </Box>
            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Time zone
                </Typography>
                <TimeZones
                  value={inputValue.timeZone}
                  onHandleDropdownChange={handleDropdownChange}
                  isEdit={isEdit}
                  name={'timeZone'}
                />
              </Box>
            </Box>
            {isNotEmpty(pathOr('', ['userName'], inputValue)) && <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Apply Link
                </Typography>
                <SummaryLink username={pathOr('', ['userName'], inputValue)} />
              </Box>
            </Box>}

            <Typography variant="h6" color="primaryColor" gutterBottom>
              Account information
            </Typography>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Email
                </Typography>
                <SummaryInput
                  value={inputValue.email}
                  onChange={handleChange}
                  type="text"
                  name="email"
                  isEdit={isEdit}
                  onClick={() => setEdit(true)}
                  onClose={handleClose}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Password
                </Typography>
                <SummaryInput
                  value={password}
                  onPasswordChange={handlePasswordChange}
                  type="password"
                  isEdit={isEdit}
                  updateErrors={updateErrors}
                  onClick={() => setEdit(true)}
                />
              </Box>
            </Box>
            {hasInAppCapability && (
              <>
                <Typography
                  color="textSecondary"
                  component="p"
                  variant="caption"
                >
                  Enable instant notifications on task completion and chats
                </Typography>
                <InAppNotificationToggle
                  enableInAppNotification={enableInAppNotification}
                  handleChangeInAppNotification={handleChangeInAppNotification}
                  isEdit={isEdit}
                />
              </>
            )}
          </Box>

          <Box className={classes.wrapper}>
            <Typography variant="h6" color="primaryColor" gutterBottom>
              Company Information
            </Typography>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Company name
                </Typography>
                <SummaryInput
                  value={inputValue.businessName}
                  onChange={handleChange}
                  type="text"
                  name="businessName"
                  isEdit={isEdit}
                  onClick={() => setEdit(true)}
                  onClose={handleClose}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Company phone number
                </Typography>
                <SummaryInput
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  type={PHONE_NUMBER_TEXT}
                  isEdit={isEdit}
                  onClick={() => setEdit(true)}
                  onClose={handleClose}
                />
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Company address
                </Typography>
                {isEdit ? <AddressDropdown
                  name="businessLocation"
                  onhandleSelect={(value: string, name: any) => {
                    handleValueChange(value, name);
                  }}
                  onChange={(name: string, value: any) => {
                    handleValueChange(value, name)
                  }}
                  value={inputValue.businessLocation}
                /> : <Typography variant="caption" component="p" fontWeight="700">
                  {inputValue.businessLocation}
                </Typography>}
              </Box>
            </Box>

            <Box className={classes.profileMetaContainer}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: '100%'
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Title
                </Typography>
                <Typography variant="caption" component="p" fontWeight="700">
                  {title}
                </Typography>
                <Typography
                  variant="caption"
                  component="p"
                  color="textSecondary"
                >
                  Roles
                </Typography>
                <Typography variant="caption" component="p" fontWeight="700">
                  {join(",")(pathOr([], ['roles_id'], accounts))}
                </Typography>
              </Box>

            </Box>
          </Box>
          <Box my={2} mx={0}>
            <Grid
              container
              display="flex"
              textAlign="center"
              sx={{
                justifyContent: isEdit && matches ? 'flex-end' : 'space-between',
                gap: isEdit && matches ? 5 : 0
              }}
            >
              {isEdit ? (
                <>
                  <Button text="Save" data-test-id={'t--button-save'} onClick={() => handleSave()} />
                  <Button text="Cancel" data-test-id={'t--button-cancel'} onClick={() => setEdit(false)} />
                </>
              ) : <Button text="Edit" data-test-id={'t--button-edit'} styles={{ marginLeft: 'auto !important' }} onClick={() => setEdit(true)} />}
            </Grid>
          </Box>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
interface StateProps {
  accounts: any;
}

const mapStateToProps: MapState<StateProps, Props> = (state) => ({
  accounts: pathOr({}, ['accounts', 'data'], state)
});

export const connected = connect(mapStateToProps);

const enhanced = compose(
  connected,
  withAdvisorProfile((ownProps, advisorProfile) => ({
    ...ownProps,
    advisorProfile,
  }))
);

export default enhanced(AdvisorProfilePopup);
